import type { ParsedUrlQuery } from 'node:querystring';

import {
  getSearchTypeAlias,
  resolveSearchTypeToCollection,
} from '@/features/SearchPage/utils/resolveSearchType';

const TYPE_SLUGS: Record<string, string> = {
  music: 'music',
  sfx: 'sfx',
  sv: 'stock-video',
  mg: 'motion-graphics',
  luts: 'luts',
};

/**
 * Path segment for any accepted search type value (alias, slug or collection
 * name): `sv` → `stock-video`. `null` when the type is unknown.
 */
export const getSearchTypeSlug = (type: string): string | null => {
  const collection = resolveSearchTypeToCollection(type);

  return collection ? TYPE_SLUGS[getSearchTypeAlias(collection)] : null;
};

// Spaces are written as `+` so paths stay readable. Next decodes the segment
// once before we see it, which would make a literal `+` indistinguishable from
// a space - so `+` and `%` are double-encoded and unwound in decodeSearchTerm.
const ENCODE: Record<string, string> = {
  '%20': '+',
  '%2B': '%252B',
  '%25': '%2525',
};
const DECODE: Record<string, string> = { '+': ' ', '%2B': '+', '%25': '%' };
const ENCODE_PATTERN = /%20|%2B|%25/g;
const DECODE_PATTERN = /\+|%2B|%25/g;

export const encodeSearchTerm = (term: string): string =>
  encodeURIComponent(term).replace(ENCODE_PATTERN, (match) => ENCODE[match]);

/** Expects the segment as Next hands it over, i.e. already decoded once. */
export const decodeSearchTerm = (segment: string): string =>
  segment.replace(DECODE_PATTERN, (match) => DECODE[match]);

/** The search term from the `/search/[type]/[[...search]]` route params. */
export const getSearchTerm = (query: ParsedUrlQuery): string => {
  const { search } = query;

  return decodeSearchTerm(
    Array.isArray(search) ? search.join('/') : (search ?? '')
  );
};

/** `/search/<library>/<term>`, with any filters as a query string. */
export const buildSearchUrl = (
  type: string,
  term: string,
  params?: Record<string, string>
): string => {
  const slug = getSearchTypeSlug(type) ?? TYPE_SLUGS.music;
  const queryString = new URLSearchParams(params).toString();
  const path = term ? `/${encodeSearchTerm(term)}` : '';

  return `/search/${slug}${path}${queryString ? `?${queryString}` : ''}`;
};
